import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API, BACKEND } from "@/lib/api";


const AuthContext = createContext(null);

export const formatApiError = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: if returning from the OAuth callback, skip the /me check —
        // AuthCallback exchanges the session_id and establishes the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const { data } = await axios.post(
            `${API}/auth/login`,
            { email, password },
            { withCredentials: true },
        );
        setUser(data);
        return data;
    };

    const signup = async (name, email, password, phone = "", smsConsent = false) => {
        const { data } = await axios.post(
            `${API}/auth/register`,
            { name, email, password, phone, sms_consent: smsConsent },
            { withCredentials: true },
        );
        setUser(data);
        return data;
    };

    const logout = async () => {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
        setUser(false);
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const loginWithGoogle = () => {
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <AuthContext.Provider
            value={{ user, loading, login, signup, logout, loginWithGoogle, setUser }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
